import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as agencyProviderPackageActions from '@redux/modules/agencyProviderPackage'
import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import defaultFormState, { requiredFields } from '@models/agencyProviderPackage'
import type { AgencyProviderPackageModel, AgencyProviderPackageRequestOptions } from '@models/types'

type BulkCreateParams = {
  agency_id?: number,
  agency_ids?: number[],
  provider_package_id?: number,
  provider_package_ids?: number[],
}

type BulkCreateAgencyProviderPackagesParams = {
  bulkCreateParams: BulkCreateParams,
  dispatch: AppDispatch,
  requestOptions?: AgencyProviderPackageRequestOptions,
}

const bulkCreateAgencyProviderPackages = (params: BulkCreateAgencyProviderPackagesParams) => {
  const { bulkCreateParams, dispatch, requestOptions } = params

  const { bulkCreateAgencyProviderPackages: bulkCreateFn } = agencyProviderPackageActions

  return dispatch(bulkCreateFn(bulkCreateParams, requestOptions))
}

type BulkDeleteParams = {
  agency_id?: number,
  agency_provider_package_ids?: number[],
  provider_package_id?: number,
}

type BulkDeleteAgencyProviderPackages = {
  bulkDeleteParams: BulkDeleteParams,
  dispatch: AppDispatch,
  requestOptions?: AgencyProviderPackageRequestOptions,
}

const bulkDeleteAgencyProviderPackages = (params: BulkDeleteAgencyProviderPackages) => {
  const { bulkDeleteParams, dispatch, requestOptions } = params
  const { bulkDeleteAgencyProviderPackages: bulkDeleteAgencyProviderPackagesFn } = agencyProviderPackageActions

  return dispatch(bulkDeleteAgencyProviderPackagesFn(bulkDeleteParams, requestOptions))
}

type CreateAgencyProviderPackageParams = {
  agencyProviderPackageParams: Partial<AgencyProviderPackageModel>,
  dispatch: AppDispatch,
  requestOptions?: AgencyProviderPackageRequestOptions,
}

const createAgencyProviderPackage = (params: CreateAgencyProviderPackageParams) => {
  const { dispatch, agencyProviderPackageParams, requestOptions } = params
  const { createAgencyProviderPackage: createFn } = agencyProviderPackageActions

  return dispatch(createFn(agencyProviderPackageParams, requestOptions))
}

type DeleteAgencyProviderPackageParams = {
  agencyProviderPackage: DeleteParams<AgencyProviderPackageModel>,
  dispatch: AppDispatch,
}

const deleteAgencyProviderPackage = (params: DeleteAgencyProviderPackageParams) => {
  const { dispatch, agencyProviderPackage } = params
  const { deleteAgencyProviderPackage: deleteFn } = agencyProviderPackageActions

  return dispatch(deleteFn(agencyProviderPackage))
}

export type ToggleVisibilityParams = {
  agencyProviderPackage: AgencyProviderPackageModel,
  dispatch: AppDispatch,
}

const toggleVisibility = (params: ToggleVisibilityParams) => {
  const { agencyProviderPackage, dispatch } = params
  const { toggleVisibility: toggleVisibilityFn } = agencyProviderPackageActions

  return dispatch(toggleVisibilityFn(agencyProviderPackage))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useAgencyProviderPackageForm(
  agencyProviderPackage: Partial<AgencyProviderPackageModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const agencyProviderPackageForm = useForm(
    defaultFormState,
    { entity: agencyProviderPackage, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [agencyProviderPackage.id, agencyProviderPackage.cache_key],
  )

  return {
    ...agencyProviderPackageForm,
  }
}

export const useRelations = (agencyProviderPackage: Partial<AgencyProviderPackageModel> = {}) => {
  const { agency_id, provider_package_id } = agencyProviderPackage || {}

  const entities = useSelector(reduxState => reduxState.entities)
  const { agencies, providerPackages, providers } = entities

  const agency = agency_id ? agencies[agency_id] || {} : {}
  const providerPackage = provider_package_id ? providerPackages[provider_package_id] || {} : {}
  // @ts-expect-error TS2339
  const provider = providerPackage.provider_id ? providers[providerPackage.provider_id] || {} : {}

  return {
    agency,
    provider,
    providerPackage,
  }
}

function useAgencyProviderPackage(initEntity: Partial<AgencyProviderPackageModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: agencyProviderPackage }: {
    entity: AgencyProviderPackageModel,
  } = useLatestEntity(initEntity, 'agencyProviderPackages')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    agency,
    provider,
    providerPackage,
  } = useRelations(agencyProviderPackage)

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.agencyProviderPackages)

  return {
    agency,
    agencyProviderPackage,
    callbacks: {
      addAgencyProviderPackages: () => launchModal({
        callbacks,
        modalKey: 'BulkCreateAgencyProviderPackagesModal',
        payload: { agencyProviderPackage },
      }),
      bulkCreateAgencyProviderPackages: (
        bulkCreateParams: BulkCreateParams,
        entityOptions?: AgencyProviderPackageRequestOptions,
      ) => (
        bulkCreateAgencyProviderPackages({ bulkCreateParams, dispatch, requestOptions: entityOptions })
      ),
      bulkDeleteAgencyProviderPackages: (
        bulkDeleteParams: BulkDeleteParams,
        requestOptions?: AgencyProviderPackageRequestOptions,
      ) => (
        bulkDeleteAgencyProviderPackages({ bulkDeleteParams, dispatch, requestOptions })
      ),
      createAgencyProviderPackage: (
        agencyProviderPackageParams: Partial<AgencyProviderPackageModel>,
        entityOptions?: AgencyProviderPackageRequestOptions,
      ) => (
        createAgencyProviderPackage({ agencyProviderPackageParams, dispatch, requestOptions: entityOptions })
      ),
      deleteAgencyProviderPackage: () => deleteAgencyProviderPackage({ dispatch, agencyProviderPackage }),
      manageAgencyProviderPackage: () => launchModal({
        callbacks,
        modalKey: 'ManageAgencyProviderPackageModal',
        payload: { agencyProviderPackage },
      }),
      toggleVisibility: () => toggleVisibility({ agencyProviderPackage, dispatch }),
    },
    creating,
    deleting,
    loading,
    provider,
    providerPackage,
    updating,
  }
}

export default useAgencyProviderPackage
